import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../utils/supabase';

interface TimeSlot {
  start_time: string;
  end_time: string;
}

interface BookingCalendarProps {
  timeslotsApi: string;
  appointmentsApi: string;
  texts: {
    heading: string;
    step0Title: string;
    step1Title: string;
    step2Title: string;
    selectDate: string;
    selectTime: string;
    reviewAndBook: string;
    previousMonth: string;
    nextMonth: string;
    noSlotsAvailable: string;
    selectedDateTime: string;
    modifyTime: string;
    customerName: string;
    customerPhone: string;
    customerEmail?: string;
    notes: string;
    submitButton: string;
    submitting: string;
    successMessage: string;
    errorMessage: string;
    backButton: string;
    required: string;
  };
  containerStyle?: string;
  onSubmitSuccess?: () => void;
}

function getCurrentYearMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function BookingCalendar(props: BookingCalendarProps) {
  const { timeslotsApi, appointmentsApi, texts, containerStyle = '', onSubmitSuccess } = props;
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [timeslots, setTimeslots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  // Always initialize to current month — never empty
  const [viewMonth, setViewMonth] = useState<string>(getCurrentYearMonth());
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const fetchTimeslots = async () => {
    setLoading(true);
    setApiError(false);
    try {
      const res = await fetch(timeslotsApi, { mode: 'cors' });
      if (!res.ok) throw new Error('API error');
      const data = await res.json();
      const slots: TimeSlot[] = Array.isArray(data) ? data : [];
      setTimeslots(slots);

      if (slots.length > 0) {
        const today = new Date().toISOString().split('T')[0];
        const availableDates = slots
          .map((slot) => slot.start_time.split(' ')[0])
          .filter(Boolean);
        const uniqueDates = Array.from(new Set(availableDates)).sort() as string[];

        const closestDate = uniqueDates.find((d) => d >= today) ?? uniqueDates[uniqueDates.length - 1];
        if (closestDate) {
          setSelectedDate(closestDate);
          setViewMonth(closestDate.substring(0, 7));
        }
      }
    } catch {
      setApiError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimeslots();
  }, [timeslotsApi]);

  const getAvailableDates = (): string[] => {
    const dates = timeslots.map((slot) => slot.start_time.split(' ')[0]).filter(Boolean);
    return Array.from(new Set(dates));
  };

  const getSlotsForDate = (date: string): TimeSlot[] =>
    timeslots.filter((slot) => slot.start_time.split(' ')[0] === date);

  const formatTime = (datetime: string): string => {
    const timePart = datetime.split(' ')[1];
    if (!timePart) return '';
    const [hourStr, minStr] = timePart.split(':');
    const hour = parseInt(hourStr, 10);
    const min = minStr ?? '00';
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 === 0 ? 12 : hour % 12;
    return `${displayHour}:${min} ${ampm}`;
  };

  const getDaysInMonth = (yearMonth: string): number => {
    const [year, month] = yearMonth.split('-').map(Number);
    return new Date(year, month, 0).getDate();
  };

  const getFirstDayOfMonth = (yearMonth: string): number => {
    const [year, month] = yearMonth.split('-').map(Number);
    return new Date(year, month - 1, 1).getDay();
  };

  const changeMonth = (offset: number) => {
    const [year, month] = viewMonth.split('-').map(Number);
    const newDate = new Date(year, month - 1 + offset, 1);
    const newYearMonth = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`;
    setViewMonth(newYearMonth);
  };

  const getMonthName = (yearMonth: string): string => {
    if (!yearMonth || !yearMonth.includes('-')) return '';
    const [year, month] = yearMonth.split('-').map(Number);
    if (!year || !month) return '';
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const renderCalendar = () => {
    if (!viewMonth || !viewMonth.includes('-')) return null;
    const daysInMonth = getDaysInMonth(viewMonth);
    const firstDay = getFirstDayOfMonth(viewMonth);
    const availableDates = getAvailableDates();
    const today = new Date().toISOString().split('T')[0];
    const weeks: JSX.Element[][] = [];
    let days: JSX.Element[] = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="aspect-square" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${viewMonth}-${String(day).padStart(2, '0')}`;
      const isAvailable = availableDates.includes(dateStr) && dateStr >= today;
      const isSelected = dateStr === selectedDate;
      const isPast = dateStr < today;

      days.push(
        <button
          key={day}
          type="button"
          disabled={!isAvailable}
          onClick={() => {
            if (isAvailable) setSelectedDate(dateStr);
          }}
          className={`aspect-square rounded-lg text-[13px] font-medium transition-all ${
            isSelected
              ? 'bg-[#D97642] text-white shadow-md'
              : isAvailable
              ? 'bg-white text-[#1A3A52] border border-gray-200 hover:border-[#D97642] hover:bg-[#D97642]/5 cursor-pointer'
              : isPast
              ? 'bg-transparent text-gray-300 cursor-not-allowed'
              : 'bg-gray-50 text-gray-300 cursor-not-allowed'
          }`}
        >
          {day}
        </button>
      );

      if ((firstDay + day) % 7 === 0 || day === daysInMonth) {
        weeks.push([...days]);
        days = [];
      }
    }

    return weeks.map((week, idx) => (
      <div key={idx} className="grid grid-cols-7 gap-1.5">
        {week}
      </div>
    ));
  };

  const handleSlotSelect = (slot: TimeSlot) => {
    setSelectedSlot(slot);
    setCurrentStep(2);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedSlot || isSubmitting) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const payload = {
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone,
        customer_email: formData.customer_email,
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        notes: formData.notes,
      };
      const res = await fetch(appointmentsApi, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        // ── Prepare navigation data FIRST ──
        const appointmentDate = selectedSlot.start_time.split(' ')[0];
        const appointmentTime = `${formatTime(selectedSlot.start_time)} – ${formatTime(selectedSlot.end_time)}`;
        const confirmationUrl = `/booking-confirmation?name=${encodeURIComponent(formData.customer_name)}&date=${encodeURIComponent(appointmentDate)}&time=${encodeURIComponent(appointmentTime)}`;

        // ── Fire-and-forget: send SMS alert to agent ──
        const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
        fetch(`${supabaseUrl}/functions/v1/send-sms-alert`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'booking_alert',
            booking: {
              customer_name: formData.customer_name,
              customer_phone: formData.customer_phone,
              customer_email: formData.customer_email,
              appointment_time: `${selectedSlot.start_time} – ${selectedSlot.end_time}`,
              notes: formData.notes || '',
            },
          }),
        }).catch(() => {});

        // ── Fire-and-forget: send confirmation + agent notification ──
        fetch(`${supabaseUrl}/functions/v1/send-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'calendar_booking',
            lead: {
              name: formData.customer_name,
              email: formData.customer_email,
              phone: formData.customer_phone,
              appointment_time: `${selectedSlot.start_time} – ${selectedSlot.end_time}`,
              message: formData.notes || '',
            },
          }),
        }).catch(() => {});

        // ── Fire-and-forget: insert lead into Supabase ──
        const nameParts = formData.customer_name.trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        const followUpTime = new Date();
        followUpTime.setHours(followUpTime.getHours() + 24);

        supabase
          .from('leads')
          .insert({
            first_name: firstName,
            last_name: lastName,
            email: formData.customer_email,
            phone: formData.customer_phone,
            notes: `Calendar booking: ${selectedSlot.start_time} – ${selectedSlot.end_time}. ${formData.notes || ''}`.trim(),
            source: 'Calendar Booking',
            stage: 'appointment_scheduled',
            follow_up_scheduled_at: followUpTime.toISOString(),
            follow_up_sent: false,
          })
          .then(() => {})
          .catch(() => {});

        // ── Call optional success callback ──
        if (onSubmitSuccess) onSubmitSuccess();

        // ── Navigate immediately - don't reset isSubmitting ──
        navigate(confirmationUrl);
        return;
      } else {
        setSubmitStatus('error');
        setIsSubmitting(false);
      }
    } catch {
      setSubmitStatus('error');
      setIsSubmitting(false);
    }
  };

  // ── Loading state ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className={containerStyle}>
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <div className="w-10 h-10 border-4 border-[#D97642] border-t-transparent rounded-full animate-spin" />
          <p className="text-[14px] text-[#718096]">Loading available dates…</p>
        </div>
      </div>
    );
  }

  // ── API error state ────────────────────────────────────────────
  if (apiError) {
    return (
      <div className={containerStyle}>
        <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-red-50">
            <i className="ri-calendar-close-line text-[28px] text-red-400" />
          </div>
          <p className="text-[16px] font-semibold text-[#1A3A52]">Calendar Unavailable</p>
          <p className="text-[14px] text-[#718096] max-w-[280px]">
            We couldn't load available times. Please call us directly to book.
          </p>
          <a
            href="tel:+13176482983"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D97642] text-white text-[14px] font-semibold rounded-lg hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-fill" />
            Call (317) 648-2983
          </a>
        </div>
      </div>
    );
  }

  // ── No slots available ─────────────────────────────────────────
  if (timeslots.length === 0) {
    return (
      <div className={containerStyle}>
        <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-amber-50">
            <i className="ri-calendar-2-line text-[28px] text-amber-400" />
          </div>
          <p className="text-[16px] font-semibold text-[#1A3A52]">No Open Slots Right Now</p>
          <p className="text-[14px] text-[#718096] max-w-[300px]">
            All appointment slots are currently filled. Call us and we'll find a time that works for you.
          </p>
          <a
            href="tel:+13176482983"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D97642] text-white text-[14px] font-semibold rounded-lg hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-fill" />
            Call (317) 648-2983
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={containerStyle}>

      {/* ── Step 0: Date Selection ── */}
      {currentStep === 0 && (
        <div>
          <h3 className="text-[18px] font-bold text-[#1A3A52] mb-5">{texts.step0Title}</h3>

          {/* Month nav */}
          <div className="mb-4 flex items-center justify-between">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => changeMonth(-1)}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-[#1A3A52] hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-[20px]" aria-hidden="true" />
            </button>
            <span className="text-[15px] font-semibold text-[#1A3A52]">{getMonthName(viewMonth)}</span>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => changeMonth(1)}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-[#1A3A52] hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-[20px]" aria-hidden="true" />
            </button>
          </div>

          {/* Day headers */}
          <div className="mb-2 grid grid-cols-7 gap-1.5">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d} className="text-center text-[11px] font-semibold text-[#718096] uppercase">
                {d}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="space-y-1.5">{renderCalendar()}</div>

          {/* Legend */}
          <div className="mt-4 flex items-center gap-4 text-[12px] text-[#718096]">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-[#D97642]" />
              <span>Selected</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-white border border-gray-200" />
              <span>Available</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-gray-100" />
              <span>Unavailable</span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              if (selectedDate && getSlotsForDate(selectedDate).length > 0) setCurrentStep(1);
            }}
            disabled={!selectedDate || getSlotsForDate(selectedDate).length === 0}
            className="w-full mt-6 py-3 bg-[#D97642] text-white text-[15px] font-semibold rounded-lg hover:bg-[#C26638] transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            {texts.selectTime}
          </button>
        </div>
      )}

      {/* ── Step 1: Time Slot Selection ── */}
      {currentStep === 1 && (
        <div>
          <h3 className="text-[18px] font-bold text-[#1A3A52] mb-1">{texts.step1Title}</h3>
          <p className="text-[13px] text-[#718096] mb-5">
            {selectedDate
              ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })
              : ''}
          </p>

          <div className="space-y-2 mb-6 max-h-[320px] overflow-y-auto pr-1">
            {getSlotsForDate(selectedDate).map((slot, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleSlotSelect(slot)}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-[14px] font-medium text-[#1A3A52] hover:border-[#D97642] hover:bg-[#D97642]/5 transition-all cursor-pointer text-left flex items-center gap-3"
              >
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className="ri-time-line text-[#D97642] text-[16px]" />
                </div>
                <span className="whitespace-nowrap">{formatTime(slot.start_time)} – {formatTime(slot.end_time)}</span>
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setCurrentStep(0)}
            className="w-full py-3 border border-gray-300 text-[#1A3A52] text-[14px] font-semibold rounded-lg hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-arrow-left-line" />
            {texts.backButton}
          </button>
        </div>
      )}

      {/* ── Step 2: Review & Form ── */}
      {currentStep === 2 && (
        <div>
          {submitStatus === 'success' ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mb-4">
                <i className="ri-check-line text-[32px] text-green-600" />
              </div>
              <h4 className="text-[20px] font-bold text-[#1A3A52] mb-2">{texts.successMessage}</h4>
              <p className="text-[14px] text-[#718096]">Check your email for a confirmation.</p>
            </div>
          ) : (
            <>
              <h3 className="text-[18px] font-bold text-[#1A3A52] mb-5">{texts.step2Title}</h3>

              {/* Selected slot summary */}
              <div className="bg-[#F9F7F4] rounded-xl p-4 mb-6 flex items-start justify-between">
                <div>
                  <p className="text-[11px] font-semibold text-[#718096] uppercase tracking-wide mb-1">
                    {texts.selectedDateTime}
                  </p>
                  <p className="text-[15px] font-bold text-[#1A3A52]">
                    {selectedSlot
                      ? new Date(selectedSlot.start_time.split(' ')[0] + 'T00:00:00').toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                        })
                      : ''}
                  </p>
                  <p className="text-[14px] text-[#4A5568] mt-0.5">
                    {selectedSlot
                      ? `${formatTime(selectedSlot.start_time)} – ${formatTime(selectedSlot.end_time)}`
                      : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setCurrentStep(1)}
                  className="text-[12px] font-semibold text-[#D97642] hover:text-[#C26638] transition-colors cursor-pointer whitespace-nowrap"
                >
                  {texts.modifyTime}
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">
                    {texts.customerName} <span className="text-[#D97642]">{texts.required}</span>
                  </label>
                  <input
                    type="text"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleFormChange}
                    required
                    placeholder="Jane Smith"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-[14px] text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">
                    {texts.customerEmail ?? 'Email Address'} <span className="text-[#D97642]">{texts.required}</span>
                  </label>
                  <input
                    type="email"
                    name="customer_email"
                    value={formData.customer_email}
                    onChange={handleFormChange}
                    required
                    placeholder="jane@example.com"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-[14px] text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">
                    {texts.customerPhone} <span className="text-[#D97642]">{texts.required}</span>
                  </label>
                  <input
                    type="tel"
                    name="customer_phone"
                    value={formData.customer_phone}
                    onChange={handleFormChange}
                    required
                    placeholder="(317) 000-0000"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-[14px] text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">
                    {texts.notes}
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleFormChange}
                    rows={3}
                    maxLength={500}
                    placeholder="Questions, health conditions, coverage goals…"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-[14px] text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors resize-none"
                  />
                </div>

                {submitStatus === 'error' && (
                  <div className="flex flex-col gap-3 p-4 bg-red-50 rounded-lg border border-red-100">
                    <p className="text-[13px] text-red-600 flex items-center gap-1.5 font-medium">
                      <i className="ri-error-warning-line" />
                      {texts.errorMessage}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <a
                        href="tel:+13176482983"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#D97642] text-white text-[12px] font-semibold rounded-lg hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-phone-fill" />
                        Call (317) 648-2983
                      </a>
                      <button
                        type="button"
                        onClick={() => setSubmitStatus('idle')}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-[#1A3A52] text-[12px] font-semibold rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-refresh-line" />
                        Try Again
                      </button>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 bg-[#D97642] text-white text-[15px] font-semibold rounded-lg hover:bg-[#C26638] transition-all hover:shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      {texts.submitting}
                    </>
                  ) : (
                    <>
                      <i className="ri-calendar-check-fill" />
                      {texts.submitButton}
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </div>
  );
}
